import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { uiConfig, crudConfig } from '../../../../conf/conceptDeterminationMethods';

// Components
import Form from '../../../../../components/Form';
import FeedbackCard from '../../../../../components/FeedbackCard';
import { Dialog } from '../../../../../components/Dialog';
import { DialogHeader } from '../../../../../components/DialogHeader';
import { DialogBody } from '../../../../../components/DialogBody';
import { DialogFooter } from '../../../../../components/DialogFooter';
import { Button } from '../../../../../components/Button';
import { Spinner } from '../../../../../components/Spinner';
import { MaterialIcon } from '../../../../../components/MaterialIcon';
import TabbedContainer from '../../../../../components/TabbedContainer';
import { ConfirmationModal } from '../../../../../components/ConfirmationModal';

// Hooks
import { useCreateConceptDeterminationMethod } from '../../../api/hooks/useCreateConceptDeterminationMethod';
import { useUpdateConceptDeterminationMethod } from '../../../api/hooks/useUpdateConceptDeterminationMethod';
import { useConceptDeterminationMethodById } from '../../../api/hooks/useConceptDeterminationMethodById';

export const SpinnerCont = styled.div`
    display: flex;
    justify-content: center;
`;

const AddEdit = ({ formModel, selectedCodeId, closeDialog, sessionToken, saveDisabled, onMount, isExistingDoc }) => {
    const [codeId, setCodeId] = React.useState(selectedCodeId);
    const isNew = React.useMemo(() => codeId === 'new', [codeId]);
    const [wasSuccessful, setWasSuccessful] = React.useState(false);
    const [buttonClicked, setButtonClicked] = React.useState();
    const CloseDialog = () => {
        return typeof closeDialog === 'function' ? closeDialog() : null;
    };
    const onClose = () => {
        if (edited) {
            setShowDiscardChangesModal(true);
        } else {
            CloseDialog();
        }
    };
    const [showDiscardChangesModal, setShowDiscardChangesModal] = React.useState(false);
    const mutationConfig = {
        crudConfig,
        sessionToken,
        onErrorCb: (e) => {
            setFatalError(e);
            setWasSuccessful(false);
        },
        onSettledCb: () => {
            setButtonClicked();
        }
    };
    const createMethod = useCreateConceptDeterminationMethod({
        ...mutationConfig,
        onSuccessCb: ({ data, context = {} }) => {
            setFatalError(null);
            setWasSuccessful(true);
            setEdited(false);
            setCodeId(data);
            if (context?.closeOnSuccess) {
                setTimeout(() => {
                    CloseDialog();
                }, 500);
            }
        }
    });
    const updateMethod = useUpdateConceptDeterminationMethod({
        ...mutationConfig,
        onSuccessCb: ({ context = {} }) => {
            setFatalError(null);
            setWasSuccessful(true);
            setEdited(false);
            if (context?.closeOnSuccess) {
                setTimeout(() => {
                    CloseDialog();
                }, 500);
            }
        }
    });
    const conceptDeterminationMethodQuery = useConceptDeterminationMethodById({
        sessionToken,
        codeId
    });
    const [compose, updateCompose] = React.useState(false);
    const [edited, setEdited] = React.useState(false);
    const [errors, setErrors] = React.useState({});
    const [fatalError, setFatalError] = React.useState(null);
    const [conceptDeterminationMethodState, setConceptDeterminationMethodState] = React.useState(null);
    React.useEffect(() => {
        if (!isNew) {
            if (conceptDeterminationMethodQuery?.isLoading) {
                setConceptDeterminationMethodState({});
                return;
            }
            if (typeof conceptDeterminationMethodQuery?.data === 'object') {
                setConceptDeterminationMethodState(conceptDeterminationMethodQuery?.data);
            } else {
                // This is an error
                alert('Subject data not found, please refresh the page');
                setConceptDeterminationMethodState({});
            }
            return;
        } else {
            setConceptDeterminationMethodState(typeof uiConfig.newDoc === 'object' ? uiConfig.newDoc : {});
        }
    }, [isNew, codeId, conceptDeterminationMethodQuery?.isLoading, conceptDeterminationMethodQuery?.data]);

    React.useEffect(() => {
        if (edited && wasSuccessful) {
            setWasSuccessful(false);
        }
    }, [edited]);

    const prepareDocument = (docState, newMethod) => {
        return {
            ...docState,
            dtoState: newMethod ? 'NEW' : 'UPDATED'
        };
    };

    const onApplyFormState = (formState) => {
        if (saveDisabled) {
            console.warn('Authoring Interface is currently read-only.');
            return;
        }
        let update = prepareDocument(
            {
                ...conceptDeterminationMethodState,
                ...formState
            },
            isNew
        );
        updateCompose(false);
        let { valid } = validate(update);
        if (!valid) return;
        if (isExistingDoc(update)) {
            setFatalError('This concept determination method already exists in the system.');
            return;
        }
        if (isNew) {
            createMethod.mutate({
                newMethod: update,
                context: { closeOnSuccess: buttonClicked === 'save' }
            });
        } else {
            updateMethod.mutate({ update, context: { closeOnSuccess: buttonClicked === 'save' } });
        }
    };

    const isLoading = React.useMemo(() => createMethod.isLoading || updateMethod.isLoading, [
        createMethod,
        updateMethod
    ]);

    // Temporary validation solution
    const validate = (formState) => {
        let errors = {};
        for (let field of formModel) {
            let { valid, failed } = field.validation(formState[field.accessor]);
            if (!valid) {
                errors[field.accessor] = 'Invalid Input: ' + failed.join(', ');
            }
            // Add additional validation here;
        }
        // Add relational validation here

        setErrors(errors);
        let valid = Object.keys(errors).length === 0;
        return { valid, failureMsg: '' };
    };

    return (
        <>
            <div style={{ height: `30rem` }}>
                <Dialog close={onClose} size="lg" visible id="concept-determination-method-manager-addEdit">
                    <DialogHeader close={onClose}>
                        {isNew ? 'Add Concept Determination Method' : `Edit ${conceptDeterminationMethodState?.code}`}
                    </DialogHeader>
                    <DialogBody>
                        {fatalError != null && (
                            <FeedbackCard
                                intent={'danger'}
                                feedback={{
                                    heading: `An error has occurred.`,
                                    message:
                                        typeof fatalError === 'string'
                                            ? fatalError
                                            : `Please wait a few moments before trying your request
again.`
                                }}
                                details={fatalError}
                            />
                        )}
                        {isNew ? 'Enter' : 'Adjust'} required information below.
                        <p style={{ margin: `1.33rem 0 0 0` }}></p>
                        {!isNew &&
                        (conceptDeterminationMethodQuery?.isLoading ||
                            conceptDeterminationMethodState === null ||
                            Object.keys(conceptDeterminationMethodState).length === 0) ? (
                            <SpinnerCont>
                                <Spinner size={'2.3rem'} color={'#1E527E'} />
                            </SpinnerCont>
                        ) : (
                            <Form
                                formModel={formModel}
                                isNew={isNew}
                                data={conceptDeterminationMethodState}
                                errors={errors}
                                compose={compose}
                                onApplyFormState={onApplyFormState}
                                setEdited={setEdited}
                                fatalError={fatalError}
                                onMount={onMount}
                            />
                        )}
                    </DialogBody>
                    <DialogFooter>
                        {isLoading && <div style={{ marginRight: '0.5rem' }}>Loading...</div>}
                        {!isLoading && wasSuccessful && (
                            <div style={{ marginRight: '0.5rem' }}>
                                <MaterialIcon icon={'done_outline'} color={'#04846D'} />
                            </div>
                        )}
                        <Button
                            text
                            appearance="primary"
                            disabled={!edited || saveDisabled}
                            onClick={() => {
                                setButtonClicked('save');
                                updateCompose(true);
                            }}
                            loading={isLoading}
                        >
                            Save Method
                        </Button>
                        <Button
                            text
                            appearance="secondary"
                            disabled={!edited || saveDisabled}
                            onClick={() => {
                                setButtonClicked('apply');
                                updateCompose(true);
                            }}
                            loading={isLoading}
                        >
                            Apply
                        </Button>
                        <Button text appearance="secondary" onClick={onClose}>
                            Cancel
                        </Button>
                    </DialogFooter>
                </Dialog>
            </div>
            {showDiscardChangesModal ? (
                <ConfirmationModal
                    header={`Discard unsaved changes?`}
                    body={
                        'You have unsaved changes which will be discarded upon closing this modal. Do you wish to proceed?'
                    }
                    confirmButtonText={'Yes, Discard Changes'}
                    confirmButtonProps={{ appearance: 'destructive', text: true }}
                    closeDialog={() => setShowDiscardChangesModal(false)}
                    onConfirm={() =>
                        new Promise((resolve) => {
                            CloseDialog();
                            resolve();
                        })
                    }
                />
            ) : null}
        </>
    );
};

export default AddEdit;

AddEdit.propTypes = {
    selectedCodeId: PropTypes.string,
    formModel: PropTypes.arrayOf(PropTypes.object),
    isNew: PropTypes.bool,
    closeDialog: PropTypes.func,
    isExistingDoc: PropTypes.func,
    onMount: PropTypes.func,
    sessionToken: PropTypes.string,
    saveDisabled: PropTypes.bool
};
